import * as THREE from "three";
import { initSkillsGrid } from "./skills-grid";
import { initAboutModal } from "./about-modal";
import { initSliderIndicator } from "./slider-indicator";
import { FONT } from "./tokens";


const MOUNT_ID = "hero-particles-root";
/** Diameter of the cursor ring div in CSS pixels. */
const CURSOR_RING_PX = 100;
/** Diameter of the shape indicator sub-ring. */
const SUB_RING_PX = 44;
/** Distance from cursor center to sub-ring center (right side). */
const SUB_RING_OFFSET = CURSOR_RING_PX / 2 + 10 + SUB_RING_PX / 2; // 82px
/** Base and hover particle sizes for the center cloud. */
const BASE_PARTICLE_SIZE = 0.055;


/** `?hero=loop` — time-only morph (production default). `?hero=interactive` — pointer attract + reform. Dev server defaults to interactive. */
type HeroMode = "loop" | "interactive";
/** `?selector=radial` — 8 shapes in a circle. `?selector=slot` — vertical carousel slot. Default (`left`) — original 3-shape vertical column. */
type SelectorMode = "left" | "radial" | "slot";
type CompanionShape = "cube" | "sphere" | "pyramid" | "cylinder" | "torus" | "octahedron" | "cone" | "helix";
const ALL_SHAPES: CompanionShape[] = ["cube", "sphere", "torus", "pyramid", "helix", "cone", "cylinder", "octahedron"];

/** Left edge of the page content container. Uses #left-edge anchor when available (Webflow);
 *  falls back to max(5vw, (vw-1200)/2) in dev. */
function containerLeft(): number {
  const anchor = document.getElementById("left-edge");
  if (anchor) return anchor.getBoundingClientRect().left;
  return Math.max(window.innerWidth * 0.05, (window.innerWidth - 1200) / 2);
}

/** Right edge of the page content container. Uses #nav-container anchor when available (Webflow);
 *  falls back to mirrored containerLeft calculation. */
function containerRight(): number {
  const anchor = document.getElementById("nav-container");
  if (anchor) return anchor.getBoundingClientRect().right;
  return window.innerWidth - Math.max(window.innerWidth * 0.05, (window.innerWidth - 1200) / 2);
}


/** True on desktop/trackpad devices; false on touch/coarse-pointer (mobile). */
const hasFinePointer = window.matchMedia("(pointer: fine)").matches;

function readHeroMode(): HeroMode {
  const raw = new URLSearchParams(window.location.search).get("hero");
  if (raw === "loop" || raw === "interactive") return raw;
  // Default: interactive on desktop, loop on mobile/touch
  return hasFinePointer ? "interactive" : "loop";
}

function readSelectorMode(): SelectorMode {
  const raw = new URLSearchParams(window.location.search).get("selector");
  if (raw === "radial") return "radial";
  if (raw === "slot")   return "slot";
  if (raw === "left")   return "left";
  return "radial";
}

/** Returns an inline SVG string for the given shape. Stroke is white; control visibility via element opacity. */
function shapeIconSVG(shape: CompanionShape, size: number): string {
  const h = size / 2;
  const pad = Math.round(size * 0.15);
  const r = h - pad;
  const attr = `width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" fill="none" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="display:block"`;
  switch (shape) {
    case "cube":
      return `<svg ${attr}><rect x="${pad}" y="${pad}" width="${size - pad * 2}" height="${size - pad * 2}" rx="1.5"/></svg>`;
    case "sphere":
      return `<svg ${attr}><circle cx="${h}" cy="${h}" r="${r}"/></svg>`;
    case "pyramid":
      return `<svg ${attr}><polygon points="${h},${pad} ${size - pad},${size - pad} ${pad},${size - pad}"/></svg>`;
    case "cylinder": {
      const ex = h - pad + 1; const ey = Math.round(size * 0.14);
      return `<svg ${attr}><ellipse cx="${h}" cy="${pad + ey}" rx="${ex}" ry="${ey}"/><line x1="${pad}" y1="${pad + ey}" x2="${pad}" y2="${size - pad - ey}"/><line x1="${size - pad}" y1="${pad + ey}" x2="${size - pad}" y2="${size - pad - ey}"/><ellipse cx="${h}" cy="${size - pad - ey}" rx="${ex}" ry="${ey}"/></svg>`;
    }
    case "torus":
      return `<svg ${attr}><circle cx="${h}" cy="${h}" r="${r}"/><circle cx="${h}" cy="${h}" r="${Math.round(r * 0.38)}"/></svg>`;
    case "octahedron":
      return `<svg ${attr}><polygon points="${h},${pad} ${size - pad},${h} ${h},${size - pad} ${pad},${h}"/></svg>`;
    case "cone": {
      const bx = pad; const by = size - pad;
      return `<svg ${attr}><line x1="${h}" y1="${pad}" x2="${bx}" y2="${by}"/><line x1="${h}" y1="${pad}" x2="${size - pad}" y2="${by}"/><path d="M${bx},${by} Q${h},${size - pad + Math.round(size * 0.12)} ${size - pad},${by}"/></svg>`;
    }
    case "helix":
      return `<svg ${attr}><path d="M${pad},${Math.round(h * 0.72)} Q${Math.round(h * 0.9)},${pad} ${h},${Math.round(h * 0.72)} Q${Math.round(h * 1.1)},${Math.round(h * 1.28)} ${size - pad},${Math.round(h * 0.72)}"/><path d="M${pad},${Math.round(h * 1.28)} Q${Math.round(h * 0.9)},${Math.round(h * 0.72)} ${h},${Math.round(h * 1.28)} Q${Math.round(h * 1.1)},${size - pad} ${size - pad},${Math.round(h * 1.28)}"/></svg>`;
  }
}

const HOLD_THRESHOLD_MS = 300;
/** Fraction of particles that can leave the center cloud. */
const MAX_SPLIT_FRACTION = 0.78;
/** Delay (ms) between hold activation and particles appearing. */
const SPLIT_DELAY_MS = 700;

function mulberry32(seed: number): () => number {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function createCloudPositions(count: number, r: number, seed: number): Float32Array {
  const rng = mulberry32(seed);
  const out = new Float32Array(count * 3);
  for (let i = 0; i < count * 3; i += 3) {
    const u = rng();
    const v = rng();
    const theta = 2 * Math.PI * u;
    const phi = Math.acos(2 * v - 1);
    const rad = r * Math.cbrt(rng());
    const sinPhi = Math.sin(phi);
    out[i] = rad * sinPhi * Math.cos(theta);
    out[i + 1] = rad * sinPhi * Math.sin(theta);
    out[i + 2] = rad * Math.cos(phi);
  }
  return out;
}

function createSphereShellPositions(count: number, r: number, seed: number): Float32Array {
  const rng = mulberry32(seed);
  const out = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const u = rng();
    const v = rng();
    const theta = 2 * Math.PI * u;
    const phi = Math.acos(2 * v - 1);
    const sinPhi = Math.sin(phi);
    out[i * 3] = r * sinPhi * Math.cos(theta);
    out[i * 3 + 1] = r * sinPhi * Math.sin(theta);
    out[i * 3 + 2] = r * Math.cos(phi);
  }
  return out;
}

function createPyramidSurfacePositions(count: number, halfExtent: number, seed: number): Float32Array {
  const rng = mulberry32(seed);
  const out = new Float32Array(count * 3);
  const apex = new THREE.Vector3(0, halfExtent, 0);
  const b0 = new THREE.Vector3(-halfExtent, -halfExtent, -halfExtent);
  const b1 = new THREE.Vector3(halfExtent, -halfExtent, -halfExtent);
  const b2 = new THREE.Vector3(halfExtent, -halfExtent, halfExtent);
  const b3 = new THREE.Vector3(-halfExtent, -halfExtent, halfExtent);
  const tmp = new THREE.Vector3();
  function sampleTriangle(a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3): THREE.Vector3 {
    const r1 = Math.sqrt(rng());
    const r2 = rng();
    const wa = 1 - r1;
    const wb = r1 * (1 - r2);
    const wc = r1 * r2;
    return tmp.set(a.x * wa + b.x * wb + c.x * wc, a.y * wa + b.y * wb + c.y * wc, a.z * wa + b.z * wb + c.z * wc);
  }
  for (let i = 0; i < count; i++) {
    const facePick = rng();
    let p: THREE.Vector3;
    if (facePick < 0.2) { p = rng() < 0.5 ? sampleTriangle(b0, b1, b2) : sampleTriangle(b0, b2, b3); }
    else if (facePick < 0.4) { p = sampleTriangle(apex, b0, b1); }
    else if (facePick < 0.6) { p = sampleTriangle(apex, b1, b2); }
    else if (facePick < 0.8) { p = sampleTriangle(apex, b2, b3); }
    else { p = sampleTriangle(apex, b3, b0); }
    out[i * 3] = p.x + (rng() - 0.5) * 0.025;
    out[i * 3 + 1] = p.y + (rng() - 0.5) * 0.025;
    out[i * 3 + 2] = p.z + (rng() - 0.5) * 0.025;
  }
  return out;
}

function createCylinderPositions(count: number, r: number, halfH: number, seed: number): Float32Array {
  const rng = mulberry32(seed);
  const out = new Float32Array(count * 3);
  const sideArea = 2 * Math.PI * r * (2 * halfH);
  const capArea = 2 * Math.PI * r * r;
  const sideFrac = sideArea / (sideArea + capArea);
  for (let i = 0; i < count; i++) {
    const theta = 2 * Math.PI * rng();
    if (rng() < sideFrac) {
      out[i * 3]     = r * Math.cos(theta);
      out[i * 3 + 1] = (rng() * 2 - 1) * halfH;
      out[i * 3 + 2] = r * Math.sin(theta);
    } else {
      const capR = r * Math.sqrt(rng());
      out[i * 3]     = capR * Math.cos(theta);
      out[i * 3 + 1] = rng() < 0.5 ? halfH : -halfH;
      out[i * 3 + 2] = capR * Math.sin(theta);
    }
  }
  return out;
}

function createTorusPositions(count: number, R: number, r: number, seed: number): Float32Array {
  const rng = mulberry32(seed);
  const out = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const theta = 2 * Math.PI * rng();
    const phi   = 2 * Math.PI * rng();
    const cosPhi = Math.cos(phi);
    out[i * 3]     = (R + r * cosPhi) * Math.cos(theta);
    out[i * 3 + 1] = r * Math.sin(phi);
    out[i * 3 + 2] = (R + r * cosPhi) * Math.sin(theta);
  }
  return out;
}

function createOctahedronPositions(count: number, r: number, seed: number): Float32Array {
  const rng = mulberry32(seed);
  const out = new Float32Array(count * 3);
  const v: [number, number, number][] = [[r,0,0],[-r,0,0],[0,r,0],[0,-r,0],[0,0,r],[0,0,-r]];
  const faces: [number,number,number][] = [[0,2,4],[0,4,3],[0,3,5],[0,5,2],[1,4,2],[1,3,4],[1,5,3],[1,2,5]];
  for (let i = 0; i < count; i++) {
    const [ai, bi, ci] = faces[Math.floor(rng() * 8)]!;
    const a = v[ai]!, b = v[bi]!, c = v[ci]!;
    const r1 = Math.sqrt(rng()), r2 = rng();
    const wa = 1 - r1, wb = r1 * (1 - r2), wc = r1 * r2;
    out[i * 3]     = a[0] * wa + b[0] * wb + c[0] * wc;
    out[i * 3 + 1] = a[1] * wa + b[1] * wb + c[1] * wc;
    out[i * 3 + 2] = a[2] * wa + b[2] * wb + c[2] * wc;
  }
  return out;
}

function createConePositions(count: number, r: number, halfH: number, seed: number): Float32Array {
  const rng = mulberry32(seed);
  const out = new Float32Array(count * 3);
  const h = 2 * halfH;
  const slant = Math.sqrt(r * r + h * h);
  const sideFrac = (Math.PI * r * slant) / (Math.PI * r * slant + Math.PI * r * r);
  for (let i = 0; i < count; i++) {
    const theta = 2 * Math.PI * rng();
    if (rng() < sideFrac) {
      const u = rng();
      const t = 1 - Math.sqrt(u); // area-weighted from apex
      out[i * 3]     = r * t * Math.cos(theta);
      out[i * 3 + 1] = halfH - t * h;
      out[i * 3 + 2] = r * t * Math.sin(theta);
    } else {
      const capR = r * Math.sqrt(rng());
      out[i * 3]     = capR * Math.cos(theta);
      out[i * 3 + 1] = -halfH;
      out[i * 3 + 2] = capR * Math.sin(theta);
    }
  }
  return out;
}

function createHelixPositions(count: number, r: number, halfH: number, turns: number, seed: number): Float32Array {
  const rng = mulberry32(seed);
  const out = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const t = rng();
    const theta = t * turns * 2 * Math.PI;
    const jr = r + (rng() - 0.5) * 0.05;
    out[i * 3]     = jr * Math.cos(theta);
    out[i * 3 + 1] = t * 2 * halfH - halfH + (rng() - 0.5) * 0.04;
    out[i * 3 + 2] = jr * Math.sin(theta);
  }
  return out;
}

function smoothstep01(t: number): number {
  const x = Math.min(1, Math.max(0, t));
  return x * x * (3 - 2 * x);
}


function createCirclePointTexture(size = 64): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const c = size / 2;
  const r = c - 1;
  ctx.clearRect(0, 0, size, size);
  const grad = ctx.createRadialGradient(c, c, 0, c, c, r);
  grad.addColorStop(0, "rgba(255,255,255,1)");
  grad.addColorStop(0.65, "rgba(255,255,255,0.35)");
  grad.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(c, c, r, 0, Math.PI * 2);
  ctx.fill();
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function createLatticePositions(grid = 10, halfExtent = 1): Float32Array {
  const n = grid * grid * grid;
  const positions = new Float32Array(n * 3);
  let p = 0;
  const span = 2 * halfExtent;
  const step = span / (grid - 1);
  for (let z = 0; z < grid; z++) {
    for (let y = 0; y < grid; y++) {
      for (let x = 0; x < grid; x++) {
        positions[p++] = -halfExtent + x * step;
        positions[p++] = -halfExtent + y * step;
        positions[p++] = -halfExtent + z * step;
      }
    }
  }
  return positions;
}

function init() {
  const root = document.getElementById(MOUNT_ID);
  if (!(root instanceof HTMLElement)) { console.error(`Missing #${MOUNT_ID}`); return; }
  const host: HTMLElement = root;

  // Inject Public Sans if not already loaded (needed when running in Webflow)
  if (!document.querySelector('link[href*="Public+Sans"]')) {
    const fontLink = document.createElement("link");
    fontLink.rel = "stylesheet";
    fontLink.href = "https://fonts.googleapis.com/css2?family=Public+Sans:wght@300;400;500&display=swap";
    document.head.appendChild(fontLink);
  }

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x080808);

  const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 100);
  camera.position.z = 4.25;

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  host.appendChild(renderer.domElement);

  host.style.boxSizing = "border-box";
  host.style.display = "block";
  host.style.width = "100%";
  host.style.maxWidth = "100%";
  host.style.margin = "0";
  host.style.overflow = "visible";
  host.style.position = "relative";
  host.style.alignSelf = "stretch";

  const canvas = renderer.domElement;
  canvas.style.display = "block";
  canvas.style.margin = "0";
  canvas.style.maxWidth = "100%";
  canvas.style.cursor = "crosshair";

  // ── Inject CSS ───────────────────────────────────────────────────────────
  const styleEl = document.createElement("style");
  styleEl.textContent = `
    #hero-cursor-ring {
      transition:
        width 0.25s ease-out,
        height 0.25s ease-out,
        background 0.25s ease-out,
        border-color 0.15s ease;
    }
    #hero-cursor-ring.ring-collapsed {
      width: 12px !important;
      height: 12px !important;
      border: none !important;
      background: #ffffff !important;
      mix-blend-mode: difference;
    }
    #hero-cursor-ring.ring-collapsed canvas {
      visibility: hidden;
    }
    #hero-cursor-ring.ring-selector-hover {
      width: 0px !important;
      height: 0px !important;
      opacity: 0 !important;
      border-width: 0 !important;
    }
    #hero-shape-indicator {
      transition: border-color 0.15s ease;
    }
    #hero-shape-indicator.ring-selector-hover {
      opacity: 0 !important;
    }
    #hero-shape-indicator.ring-collapsed {
      opacity: 0 !important;
    }
    #hero-shape-selector div:hover svg {
      opacity: 0.8 !important;
    }
  `;
  document.head.appendChild(styleEl);

  // ── HUD — fixed, follows cursor ring ─────────────────────────────────────
  const hud = document.createElement("div");
  hud.style.cssText = [
    "position:fixed",
    `font-family:${FONT.family}`,
    "font-size:12px",
    "font-weight:300",
    "letter-spacing:0.04em",
    "color:rgba(255,255,255,0.82)",
    "z-index:10000",
    "pointer-events:none",
    "transform:translateX(-50%)",
    "text-align:center",
    "white-space:nowrap",
    "display:none",
    "transition:opacity 0.25s ease",
    "background:rgba(0,0,0,0.65)",
    "padding:5px 12px",
    "border-radius:100px",
  ].join(";");
  hud.textContent = "Idle";
  document.body.appendChild(hud);


  // ── Geometry constants ───────────────────────────────────────────────────
  const latticeHalfExtent = 1.18;
  const pointsScale = 0.6 / latticeHalfExtent;
  const pointsVerticalBias = hasFinePointer ? -0.13 : 0.0;

  const grid = 10;
  const lattice = createLatticePositions(grid, latticeHalfExtent);
  const cloud = createCloudPositions(lattice.length / 3, latticeHalfExtent * 1.05, 0x9e3779b9);
  const totalParticles = cloud.length / 3;
  const maxSplitCount = Math.floor(totalParticles * MAX_SPLIT_FRACTION);
  const OFFSCREEN = 9999;

  const splitOrder = new Uint16Array(totalParticles);
  const splitRank = new Uint16Array(totalParticles);
  for (let i = 0; i < totalParticles; i++) splitOrder[i] = i;
  const splitRng = mulberry32(0x1234abcd);
  for (let i = totalParticles - 1; i > 0; i--) {
    const j = Math.floor(splitRng() * (i + 1));
    const tmp = splitOrder[i]!; splitOrder[i] = splitOrder[j]!; splitOrder[j] = tmp;
  }
  for (let i = 0; i < totalParticles; i++) splitRank[splitOrder[i]!] = i;

  // ── Companion shape data ─────────────────────────────────────────────────
  const companionCloudSource = createCloudPositions(maxSplitCount, 0.55, 0xdeadbeef);

  const companionCubeSource = createLatticePositions(7, 0.6);
  const companionCube = new Float32Array(maxSplitCount * 3);
  for (let i = 0; i < maxSplitCount; i++) {
    const src = i % (companionCubeSource.length / 3);
    companionCube[i * 3] = companionCubeSource[src * 3]!;
    companionCube[i * 3 + 1] = companionCubeSource[src * 3 + 1]!;
    companionCube[i * 3 + 2] = companionCubeSource[src * 3 + 2]!;
  }
  const companionSphere    = createSphereShellPositions(maxSplitCount, 0.52, 0xbead1234);
  const companionPyramid   = createPyramidSurfacePositions(maxSplitCount, 0.62, 0xf00d1234);
  const companionCylinder  = createCylinderPositions(maxSplitCount, 0.46, 0.54, 0xc11d1234);
  const companionTorus     = createTorusPositions(maxSplitCount, 0.40, 0.14, 0x70a51234);
  const companionOctahedron = createOctahedronPositions(maxSplitCount, 0.56, 0x8fa31234);
  const companionCone      = createConePositions(maxSplitCount, 0.50, 0.56, 0xc0de1234);
  const companionHelix     = createHelixPositions(maxSplitCount, 0.34, 0.54, 3, 0x1eaf1234);
  const companionTargets: Record<CompanionShape, Float32Array> = {
    cube:       companionCube,
    sphere:     companionSphere,
    pyramid:    companionPyramid,
    cylinder:   companionCylinder,
    torus:      companionTorus,
    octahedron: companionOctahedron,
    cone:       companionCone,
    helix:      companionHelix,
  };
  /** No shape selected by default — interaction is gated until a shape is chosen. */
  let selectedShape: CompanionShape | null = null;

  // ── Hero sphere — sphere lock (loop mode) + mobile loop ──────────────────
  // Scatter start positions — particles begin here and converge to cloud on load
  const heroSphereScatter = createCloudPositions(totalParticles, latticeHalfExtent * 3.0, 0x5ca77e3f);
  // Mobile loop shapes — allocated on coarse-pointer devices only.
  // Sequence: cube → torus → pyramid → helix → cylinder → sphere
  // Sphere is last to maximise distance from the sphere lock intro.
  // Loop shape positions — shared by desktop and mobile loop mode.
  // Sequence: cube → helix → torus → pyramid → octahedron → cone → cylinder (sphere excluded — too similar to cloud)
  const loopHelix    = createHelixPositions(totalParticles, 0.75, 1.0, 3, 0x1eaf0004);
  const loopTorus    = createTorusPositions(totalParticles, 0.78, 0.26, 0x70a50002);
  const loopPyramid  = createPyramidSurfacePositions(totalParticles, 1.1, 0xf00d0003);
  const loopOcta     = createOctahedronPositions(totalParticles, 0.98, 0x8fa30007);
  const loopCone     = createConePositions(totalParticles, 0.88, 0.98, 0xc0de0006);
  const loopCylinder = createCylinderPositions(totalParticles, 0.75, 0.9, 0xc11d0005);
  const loopShapes: Float32Array[] = [lattice, loopHelix, loopTorus, loopPyramid, loopOcta, loopCone, loopCylinder];
  // Phase durations: 4s desktop / mobile hold + rest reduced to 1.5s (was 3s) for faster cycling
  const LOOP_IN   = hasFinePointer ? 4.0 : 3.0;  // transition duration (shape → shape)
  const LOOP_HOLD = hasFinePointer ? 4.0 : 1.5;  // hold duration (fully formed)
  const LOOP_REST = hasFinePointer ? 4.0 : 1.5;  // initial cloud hold before cycle starts
  // Direct shape-to-shape: each cycle is hold + transition (no cloud return between shapes).
  // First beat is cloud → first shape; after that shapes blend directly into each other.
  const LOOP_CYCLE = LOOP_HOLD + LOOP_IN;
  function loopMorphBlend(t: number): { shapeA: Float32Array; shapeB: Float32Array; w: number } {
    // Initial cloud → first shape transition
    if (t < LOOP_IN) {
      return { shapeA: cloud, shapeB: loopShapes[0]!, w: smoothstep01(t / LOOP_IN) };
    }
    const cycleMorphT = t - LOOP_IN;
    const idx    = Math.floor(cycleMorphT / LOOP_CYCLE) % loopShapes.length;
    const phase  = cycleMorphT % LOOP_CYCLE;
    const shapeA = loopShapes[idx]!;
    const shapeB = loopShapes[(idx + 1) % loopShapes.length]!;
    const w = phase < LOOP_HOLD ? 0 : smoothstep01((phase - LOOP_HOLD) / LOOP_IN);
    return { shapeA, shapeB, w };
  }

  // ── Center cloud vectors ─────────────────────────────────────────────────
  const baseCloudVectors = new Array<THREE.Vector3>(totalParticles);
  for (let i = 0; i < totalParticles; i++) {
    baseCloudVectors[i] = new THREE.Vector3(cloud[i * 3]!, cloud[i * 3 + 1]!, cloud[i * 3 + 2]!);
  }
  const tempCenter = new THREE.Vector3();
  const centerRotEuler = new THREE.Euler();

  // ── Three.js — center cloud only ─────────────────────────────────────────
  const pointTexture = createCirclePointTexture(64);
  const centerMaterial = new THREE.PointsMaterial({
    color: 0xffffff, map: pointTexture, size: BASE_PARTICLE_SIZE, sizeAttenuation: true,
    transparent: true, opacity: 1, depthWrite: false, blending: THREE.NormalBlending,
  });
  const centerGeometry = new THREE.BufferGeometry();
  const centerPositionAttr = new THREE.BufferAttribute(new Float32Array(totalParticles * 3), 3);
  centerGeometry.setAttribute("position", centerPositionAttr);
  const centerPoints = new THREE.Points(centerGeometry, centerMaterial);
  scene.add(centerPoints);

  const heroMode = readHeroMode();
  if (heroMode === "interactive" && hasFinePointer) host.style.touchAction = "none";

  const clock = new THREE.Clock();
  let heroReadyFired = false;

  // ── Sphere build-up — converge → hold → release (loop mode only) ─────────
  // Convergence duration matches transition speed; rest gives the initial cloud hold before cycle starts.
  const SPHERE_BUILD_S       = LOOP_IN;
  const MORPH_INITIAL_REST_S = LOOP_REST;
  let sphereLockStartMs = -1;        // -1 = not triggered
  let morphActive = false;           // morph cycle is frozen until sphere lock completes
  let morphTimeOffset = 0;           // t value when lock ends; cycle starts from here

  let holdStartMs = 0;
  let holdActive = false;
  let wasHoldActive = false;
  let holdActivatedAt = 0;
  let splitProgress = 0;
  let sphereHoverProgress = 0;
  let activePointerId: number | null = null;

  // ── Cursor ring div + companion 2D canvas + shape indicator sub-ring ──────
  let cursorDiv: HTMLDivElement | null = null;
  let subRingDiv: HTMLDivElement | null = null;
  let companionCanvas: HTMLCanvasElement | null = null;
  let companionCtx: CanvasRenderingContext2D | null = null;
  let cursorX = window.innerWidth / 2;
  let cursorY = window.innerHeight / 2;
  let cursorTargetX = cursorX;
  let cursorTargetY = cursorY;
  let isInHero = true;
  let cursorVisible = false;
  let inModal = false;
  let overVideo = false;
  let overBio = false;
  let hoverInfoLabel: string | null = null;

  // Clean up stale cursor rings from legacy Webflow Page Settings embed
  document.getElementById("hero-cursor-ring")?.remove();
  document.getElementById("custom-cursor")?.remove();

  if (hasFinePointer && heroMode === "interactive") {
    // Main cursor ring
    cursorDiv = document.createElement("div");
    cursorDiv.id = "hero-cursor-ring";
    cursorDiv.style.cssText = [
      "position:fixed",
      `width:${CURSOR_RING_PX}px`,
      `height:${CURSOR_RING_PX}px`,
      "background:transparent",
      "border:1px solid rgba(255,255,255,0.55)",
      "border-radius:50%",
      "overflow:hidden",
      "pointer-events:none",
      "z-index:9999",
      "transform:translate(-50%,-50%)",
      "opacity:0",
      "transition:opacity 300ms ease",
      "will-change:left,top",
    ].join(";");

    companionCanvas = document.createElement("canvas");
    companionCanvas.width = CURSOR_RING_PX;
    companionCanvas.height = CURSOR_RING_PX;
    companionCanvas.style.cssText = `position:absolute;width:${CURSOR_RING_PX}px;height:${CURSOR_RING_PX}px;top:50%;left:50%;transform:translate(-50%,-50%);`;
    companionCtx = companionCanvas.getContext("2d");
    cursorDiv.appendChild(companionCanvas);
    document.body.appendChild(cursorDiv);

    // Shape indicator sub-ring (44px, right of main ring)
    subRingDiv = document.createElement("div");
    subRingDiv.id = "hero-shape-indicator";
    subRingDiv.style.cssText = [
      "position:fixed",
      `width:${SUB_RING_PX}px`,
      `height:${SUB_RING_PX}px`,
      "background:transparent",
      "border:1px solid rgba(255,255,255,0.25)",
      "border-radius:50%",
      "pointer-events:none",
      "z-index:9998",
      "transform:translate(-50%,-50%)",
      "opacity:0",
      "transition:opacity 300ms ease",
      "will-change:left,top",
      "display:flex",
      "align-items:center",
      "justify-content:center",
    ].join(";");
    document.body.appendChild(subRingDiv);

    // ── Hero section enter/leave ──────────────────────────────────────────
    const heroSection = host.parentElement || host;
    heroSection.addEventListener("mouseenter", () => {
      isInHero = true;
      if (cursorDiv) cursorDiv.classList.remove("ring-collapsed");
      if (subRingDiv) subRingDiv.classList.remove("ring-collapsed");
    });
    heroSection.addEventListener("mouseleave", () => {
      isInHero = false;
      if (cursorDiv) cursorDiv.classList.add("ring-collapsed");
      if (subRingDiv) subRingDiv.classList.add("ring-collapsed");
      hud.style.display = "none";
    });

    // Cursor visibility — tick() owns opacity; we just track whether cursor is in window
    window.addEventListener("mousemove", (e) => {
      cursorTargetX = e.clientX;
      cursorTargetY = e.clientY;
      cursorVisible = true;
    });
    window.addEventListener("mouseleave", () => {
      cursorVisible = false;
      if (cursorDiv) cursorDiv.style.opacity = "0";
      if (subRingDiv) subRingDiv.style.opacity = "0";
    });
  }

  // ── Shape selector ───────────────────────────────────────────────────────
  const shapeSelectorDots: HTMLElement[] = [];

  function updateShapeSelector() {
    shapeSelectorDots.forEach(dot => {
      const active = dot.dataset.heroShape === selectedShape;
      const svg = dot.querySelector("svg") as HTMLElement | null;
      if (selectedShape === null) {
        // No selection — all icons equally bright
        if (svg) svg.style.opacity = "1";
        if (selectorMode === "radial") {
          dot.style.border = "1px solid rgba(255,255,255,0.85)";
        }
      } else {
        // Shape selected — active bright, others dimmed
        if (svg) svg.style.opacity = active ? "1" : "0.35";
        if (selectorMode === "radial") {
          dot.style.border = active
            ? "1px solid rgba(255,255,255,0.85)"
            : "1px solid rgba(255,255,255,0.35)";
        }
      }
    });
    // Slot mode: also update label text
    if (selectorMode === "slot" && slotLabel) {
      slotLabel.textContent = selectedShape ? selectedShape.toUpperCase() : "SCROLL TO SELECT";
    }
  }

  function updateShapeIndicator() {
    if (!subRingDiv) return;
    if (selectedShape === null) {
      subRingDiv.innerHTML = "";
      subRingDiv.style.borderColor = "rgba(255,255,255,0.25)";
    } else {
      subRingDiv.innerHTML = shapeIconSVG(selectedShape, 18);
      subRingDiv.style.borderColor = "rgba(255,255,255,0.55)";
    }
  }

  const selectorMode = readSelectorMode();
  /** Orbital radius as fraction of min(canvasW, canvasH) — radial mode only. */
  const RADIAL_RADIUS_FRAC = 0.25;
  let radialAngleCurrent  = 0;
  let radialVelocity      = 0;
  const RADIAL_INPUT_SCALE = 2;   // velocity boost per scroll unit (tune feel here)
  const RADIAL_FRICTION    = 1.0;  // exponential decay rate — higher = snappier stop
  let spinInVelocity       = 0;
  const SPIN_IN_FRICTION   = 0.3;  // slower decay — only for the loader spin-in effect
  /** Slot carousel — px height of each shape item in the track. */
  const SLOT_ITEM_H   = 72;
  const SLOT_ICON_SZ  = 36;
  let slotRawTarget   = 0;
  let slotCurrentY    = 0;
  let slotSnapTimer: ReturnType<typeof setTimeout> | null = null;
  let slotTrack: HTMLElement | null = null;
  let slotLabel: HTMLElement | null = null;
  /** Scroll-gating state (radial mode only) */
  const SCROLL_UNLOCK_RAD = 1.0 * 2 * Math.PI; // ~6.28 rad = 1 full rotation (3 dots)
  let scrollTotalRot    = 0;
  let scrollUnlocked    = false;
  let isHeroVisible     = true;
  let scrollProgressBar: HTMLDivElement | null = null;
  const scrollDotFills: HTMLDivElement[] = [];  // fill layer per dot (3)
  const scrollDotEls:   HTMLDivElement[] = [];  // outer dot per dot (for glow)

  function repositionRadialDots() {
    if (selectorMode !== "radial" || shapeSelectorDots.length === 0) return;
    const w = host.clientWidth  || window.innerWidth;
    const h2 = host.clientHeight || window.innerHeight;
    const cx = w / 2;
    // Offset center downward slightly to match cloud vertical bias
    const cy = h2 / 2 + h2 * 0.033;
    const R  = Math.min(w, h2) * RADIAL_RADIUS_FRAC;
    shapeSelectorDots.forEach((dot, i) => {
      const angle = -Math.PI / 2 + i * (2 * Math.PI / 8) + radialAngleCurrent;
      dot.style.left = (cx + R * Math.cos(angle)) + "px";
      dot.style.top  = (cy + R * Math.sin(angle)) + "px";
    });
  }

  let hoverShapeName: string | null = null;

  if (hasFinePointer && heroMode === "interactive") {
    function collapseRings(shapeName?: string) {
      if (cursorDiv) cursorDiv.classList.add("ring-selector-hover");
      if (subRingDiv) subRingDiv.classList.add("ring-selector-hover");
      hoverShapeName = shapeName ?? null;
      // HUD text is set by tick() reading hoverShapeName; keep opacity up
      hud.style.opacity = "1";
    }
    function restoreRings() {
      if (cursorDiv) cursorDiv.classList.remove("ring-selector-hover");
      if (subRingDiv) subRingDiv.classList.remove("ring-selector-hover");
      hoverShapeName = null;
      hud.style.opacity = "1";
    }

    if (selectorMode === "left") {
      // ── Original left-column selector (3 shapes) ────────────────────────
      const selectorDiv = document.createElement("div");
      selectorDiv.id = "hero-shape-selector";
      selectorDiv.style.cssText = [
        "position:absolute",
        "left:" + containerLeft() + "px",
        "top:50%",
        "transform:translateY(-50%)",
        "display:flex",
        "flex-direction:column",
        "align-items:center",
        "gap:12px",
        "z-index:20",
        "pointer-events:all",
        "padding:16px",
        "cursor:default",
      ].join(";");

      const leftShapes: CompanionShape[] = ["cube", "sphere", "pyramid"];
      leftShapes.forEach(shape => {
        const dot = document.createElement("div");
        dot.dataset.heroShape = shape;
        dot.style.cssText = "width:22px;height:22px;cursor:pointer;pointer-events:all;display:flex;align-items:center;justify-content:center;";
        dot.innerHTML = shapeIconSVG(shape, 22);
        const svg = dot.querySelector("svg") as HTMLElement | null;
        if (svg) { svg.style.opacity = "0.4"; svg.style.transition = "opacity 0.2s ease"; }
        dot.addEventListener("mouseenter", () => collapseRings(shape.charAt(0).toUpperCase() + shape.slice(1)));
        dot.addEventListener("mouseleave", restoreRings);
        selectorDiv.appendChild(dot);
        shapeSelectorDots.push(dot);
      });
      host.appendChild(selectorDiv);

    } else if (selectorMode === "slot") {
      // ── Slot carousel selector ───────────────────────────────────────────
      const slotContainer = document.createElement("div");
      slotContainer.id = "hero-shape-slot";
      slotContainer.style.cssText = [
        "position:absolute",
        "right:" + (window.innerWidth - containerRight()) + "px",
        "top:50%",
        "transform:translateY(-50%)",
        "width:120px",
        "z-index:20",
        "pointer-events:all",
        "cursor:default",
      ].join(";");

      // Top rule
      const ruleTop = document.createElement("div");
      ruleTop.style.cssText = "height:1px;background:rgba(255,255,255,0.35);margin-bottom:0;";
      slotContainer.appendChild(ruleTop);

      // Clipping window
      const slotWindow = document.createElement("div");
      slotWindow.style.cssText = `height:${SLOT_ITEM_H}px;overflow:hidden;position:relative;`;
      slotContainer.appendChild(slotWindow);

      // Track (scrolls inside window)
      const track = document.createElement("div");
      track.id = "slot-track";
      track.style.cssText = "position:absolute;width:100%;will-change:transform;";
      slotWindow.appendChild(track);
      slotTrack = track;

      // Shape items
      ALL_SHAPES.forEach(shape => {
        const item = document.createElement("div");
        item.dataset.heroShape = shape;
        item.style.cssText = [
          `height:${SLOT_ITEM_H}px`,
          "display:flex",
          "align-items:center",
          "justify-content:center",
          "cursor:pointer",
          "pointer-events:all",
        ].join(";");
        item.innerHTML = shapeIconSVG(shape, SLOT_ICON_SZ);
        const svg = item.querySelector("svg") as HTMLElement | null;
        if (svg) { svg.style.opacity = "0.35"; svg.style.transition = "opacity 0.2s ease"; }
        track.appendChild(item);
        shapeSelectorDots.push(item);
      });

      // Bottom rule
      const ruleBottom = document.createElement("div");
      ruleBottom.style.cssText = "height:1px;background:rgba(255,255,255,0.35);";
      slotContainer.appendChild(ruleBottom);

      // Label
      const label = document.createElement("div");
      label.id = "slot-label";
      label.style.cssText = [
        "text-align:center",
        `font-family:${FONT.family}`,
        "font-size:10px",
        "letter-spacing:0.1em",
        "color:rgba(255,255,255,0.45)",
        "margin-top:10px",
        "text-transform:uppercase",
        "white-space:nowrap",
      ].join(";");
      label.textContent = "SCROLL TO SELECT";
      slotContainer.appendChild(label);
      slotLabel = label;

      host.appendChild(slotContainer);

      // Wheel listener — prevent page scroll, drive track
      host.addEventListener("wheel", (e) => {
        e.preventDefault();
        slotRawTarget += e.deltaY;
        if (slotSnapTimer) clearTimeout(slotSnapTimer);
        slotSnapTimer = setTimeout(() => {
          slotRawTarget = Math.round(slotRawTarget / SLOT_ITEM_H) * SLOT_ITEM_H;
        }, 350);
      }, { passive: false });

    } else {
      // ── Radial selector (8 shapes in a circle) ──────────────────────────
      ALL_SHAPES.forEach(shape => {
        const dot = document.createElement("div");
        dot.dataset.heroShape = shape;
        dot.style.cssText = [
          "position:absolute",
          "width:44px",
          "height:44px",
          "cursor:pointer",
          "pointer-events:all",
          "display:flex",
          "align-items:center",
          "justify-content:center",
          "z-index:20",
          "transform:translate(-50%,-50%)",
          "border-radius:50%",
          "background:#000000",
          "border:1px solid rgba(255,255,255,0.35)",
        ].join(";");
        dot.innerHTML = shapeIconSVG(shape, 22);
        const svg = dot.querySelector("svg") as HTMLElement | null;
        if (svg) { svg.style.opacity = "1"; svg.style.transition = "opacity 0.2s ease"; }
        dot.addEventListener("mouseenter", () => collapseRings(shape.charAt(0).toUpperCase() + shape.slice(1)));
        dot.addEventListener("mouseleave", restoreRings);
        // Start hidden — staggered fade-in fires on loader-fading
        dot.style.opacity = "0";
        dot.style.pointerEvents = "none";
        host.appendChild(dot);
        shapeSelectorDots.push(dot);
      });
      repositionRadialDots();

      // ── Post-hero section hide/reveal (must capture BEFORE appending our own elements to body) ──
      // Hides all body siblings after the hero so the page is exactly 100vh — Webflow's smooth
      // scroll has nowhere to go. Revealed on unlock; re-hidden when hero re-enters view.
      function getPostHeroSections(): HTMLElement[] {
        // Webflow wraps all page sections in a .body-wrap div, not directly in <body>.
        // Walk up to the direct child of .body-wrap (not document.body) so siblings
        // are the other page sections, not other body-level elements.
        const pageWrap = document.querySelector<HTMLElement>(".body-wrap") ?? document.body;
        let el: HTMLElement | null = host;
        while (el && el.parentElement !== pageWrap) el = el.parentElement;
        if (!el) return [];
        const out: HTMLElement[] = [];
        let sib = el.nextElementSibling as HTMLElement | null;
        while (sib) {
          // Only include page content sections — skip fixed-position overlays (cursor ring, modal, HUD, etc.)
          if (window.getComputedStyle(sib).position !== "fixed") out.push(sib);
          sib = sib.nextElementSibling as HTMLElement | null;
        }
        return out;
      }
      const postHeroSections = getPostHeroSections(); // captured before progress bar appended
      postHeroSections.forEach(s => { s.style.display = "none"; });
      document.documentElement.style.overscrollBehavior = "none";
      document.body.style.overscrollBehavior = "none";

      const pageWrap = document.querySelector<HTMLElement>(".body-wrap") ?? document.body;
      function scrollToTop() {
        window.scrollTo(0, 0);
        pageWrap.scrollTop = 0;
        // If Webflow Lenis smooth scroll is running, reset it too
        (window as any).lenis?.scrollTo(0, { immediate: true });
      }
      function revealSections() {
        postHeroSections.forEach(s => { s.style.display = ""; });
        document.documentElement.style.overscrollBehavior = "";
        document.body.style.overscrollBehavior = "";
        pageWrap.style.overscrollBehavior = "";
      }
      function hideSections() {
        scrollToTop();
        postHeroSections.forEach(s => { s.style.display = "none"; });
        document.documentElement.style.overscrollBehavior = "none";
        document.body.style.overscrollBehavior = "none";
        pageWrap.style.overscrollBehavior = "none";
      }

      // ── Scroll progress dots (bottom-right, aligned to container right edge) ──
      // Three stacked circles, each filling top→bottom as one rotation completes.
      scrollProgressBar = document.createElement("div");
      scrollProgressBar.id = "hero-scroll-progress";
      scrollProgressBar.style.cssText = [
        "position:fixed",
        `right:${window.innerWidth - containerRight()}px`,
        "bottom:48px",
        "display:flex",
        "flex-direction:column",
        "align-items:center",
        "gap:10px",
        "z-index:9990",
        "pointer-events:none",
        "transition:opacity 0.4s ease",
      ].join(";");
      for (let di = 0; di < 3; di++) {
        // Outer dot — border-radius + overflow clips the fill; box-shadow for glow
        const dot = document.createElement("div");
        dot.style.cssText = [
          "position:relative",
          "width:8px",
          "height:8px",
          "border-radius:50%",
          "overflow:hidden",
          "outline:1px solid #ffffff",  // ring visible in empty state
          "transition:box-shadow 0.15s ease",
        ].join(";");
        // Fill layer — slides down from above; starts fully hidden above dot
        const fill = document.createElement("div");
        fill.style.cssText = [
          "position:absolute",
          "inset:0",
          "background:#ffffff",
          "border-radius:50%",
          "transform:translateY(-100%)",
        ].join(";");
        dot.appendChild(fill);
        scrollProgressBar.appendChild(dot);
        scrollDotEls.push(dot);
        scrollDotFills.push(fill);
      }
      document.body.appendChild(scrollProgressBar);

      // Helper — update dots from total rotation
      function updateScrollDots() {
        const rotPerDot = SCROLL_UNLOCK_RAD / 3;
        scrollDotFills.forEach((fill, di) => {
          const dotProgress = Math.max(0, Math.min(1, (scrollTotalRot - di * rotPerDot) / rotPerDot));
          // translateY(-100%) = empty, translateY(0%) = full (slides down into dot)
          fill.style.transform = `translateY(${((1 - dotProgress) * -100).toFixed(1)}%)`;
        });
      }

      // Scroll-driven ring rotation — wheel reads deltaY for ring animation only.
      // { passive: false } + preventDefault while gated prevents browser elastic/bounce scroll.
      window.addEventListener("wheel", (e) => {
        if (scrollUnlocked || !isHeroVisible || modalCtrl.isOpen()) return;
        e.preventDefault();
        const delta = e.deltaY * 0.002;
        radialVelocity += delta * RADIAL_INPUT_SCALE;
        scrollTotalRot = Math.max(0, Math.min(SCROLL_UNLOCK_RAD, scrollTotalRot + delta));
        updateScrollDots();

        if (scrollTotalRot >= SCROLL_UNLOCK_RAD) {
          scrollUnlocked = true;
          scrollToTop(); // clear any accumulated scroll position before revealing
          revealSections();
          // Brief glow on all dots, then fade out the whole indicator
          scrollDotEls.forEach(dot => {
            dot.style.boxShadow = "0 0 7px 3px rgba(255,255,255,0.65)";
          });
          setTimeout(() => {
            scrollDotEls.forEach(dot => {
              dot.style.transition = "box-shadow 1.2s ease";
              dot.style.boxShadow = "none";
            });
          }, 220);
          setTimeout(() => {
            if (scrollProgressBar) scrollProgressBar.style.opacity = "0";
          }, 900);
        }
      }, { passive: false });

      // Track hero visibility. On re-entry (0.98 threshold = nearly fully visible = scrollY ≈ 0),
      // snap scroll to top, re-hide sections, and reset gate so there's no jump.
      const scrollResetObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          isHeroVisible = entry.isIntersecting;
          if (entry.isIntersecting && scrollUnlocked) {
            scrollToTop(); // snap to top before hiding so page height change is seamless
            hideSections();
            scrollTotalRot = 0;
            scrollUnlocked = false;
            updateScrollDots();
            scrollDotEls.forEach(dot => {
              dot.style.transition = "box-shadow 0.15s ease";
              dot.style.boxShadow = "none";
            });
            if (scrollProgressBar) scrollProgressBar.style.opacity = "1";
          }
        });
      }, { threshold: 1.0 });
      scrollResetObserver.observe(host);
    }

    updateShapeSelector();
    updateShapeIndicator();

    // ── Staggered selector fade-in on loader handoff (radial only) ──────────
    // Selectors fade in clockwise from 12 o'clock (index 0), starting ~1s before
    // convergence completes so they arrive just as the cloud settles.
    if (selectorMode === 'radial') {
      const STAGGER_START_MS  = Math.max(0, (SPHERE_BUILD_S - 1.0)) * 1000; // begin 1s before convergence ends
      const STAGGER_INTERVAL_MS = 150;  // ms between each icon appearing
      const STAGGER_FADE_MS     = 400;  // fade duration per icon
      window.addEventListener('loader-fading', (e) => {
        // Ring rotates in while icons stagger-fade — both start simultaneously
        const dir = Math.sign((e as CustomEvent<{ rotSpeedY: number }>).detail?.rotSpeedY ?? 1) || 1;
        spinInVelocity = dir * 4.0;
        shapeSelectorDots.forEach((dot, i) => {
          setTimeout(() => {
            dot.style.transition = `opacity ${STAGGER_FADE_MS}ms ease`;
            dot.style.opacity = "1";
            dot.style.pointerEvents = "all";
          }, STAGGER_START_MS + i * STAGGER_INTERVAL_MS);
        });
      }, { once: true });
    }
  }

  // ── Sphere build-up trigger — both loop and interactive modes ───────────────
  // On loader-fading: particles converge from scatter positions into cloud.
  // Loop mode: morph cycle activates after convergence.
  // Interactive mode: cloud is ready for interaction after convergence.
  let convergenceStarted = false;
  window.addEventListener('loader-fading', () => {
    if (!convergenceStarted) { convergenceStarted = true; sphereLockStartMs = performance.now(); }
  }, { once: true });
  // Fallback: if loader-fading never fires, trigger convergence once at 12s.
  setTimeout(() => {
    if (!convergenceStarted) { convergenceStarted = true; sphereLockStartMs = performance.now(); }
  }, 12000);

  // ── Shape selector click — toggle deselect ───────────────────────────────
  window.addEventListener("click", (e) => {
    const el = (e.target as HTMLElement | null)?.closest?.("[data-hero-shape]");
    if (!(el instanceof HTMLElement)) return;
    const raw = (el.dataset.heroShape || "").toLowerCase() as CompanionShape;
    if (ALL_SHAPES.includes(raw)) {
      selectedShape = raw === selectedShape ? null : raw;
      updateShapeSelector();
      updateShapeIndicator();
    }
  });
  const paramShape = new URLSearchParams(window.location.search).get("shape") as CompanionShape | null;
  if (paramShape && ALL_SHAPES.includes(paramShape)) {
    selectedShape = paramShape;
    updateShapeSelector();
    updateShapeIndicator();
  }

  // ── Pointer events (hold detection only) ────────────────────────────────
  if (heroMode === "interactive") {
    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      if ((e.target as HTMLElement | null)?.closest?.("[data-hero-shape]")) return;
      if (activePointerId !== null && e.pointerId !== activePointerId) return;
      activePointerId = e.pointerId;
      holdStartMs = performance.now();
      holdActive = false;
      host.setPointerCapture(e.pointerId);
    };
    const endPointer = (e: PointerEvent) => {
      if (e.pointerId !== activePointerId) return;
      activePointerId = null;
      holdActive = false;
      try { host.releasePointerCapture(e.pointerId); } catch { /* already released */ }
    };
    const cap = { capture: true };
    host.addEventListener("pointerdown", onPointerDown, cap);
    host.addEventListener("pointerup", endPointer, cap);
    host.addEventListener("pointercancel", endPointer, cap);
  }

  // ── Resize ───────────────────────────────────────────────────────────────
  function resize() {
    const left = containerLeft() + "px";
    if (selectorMode === "left") {
      const sel = document.getElementById("hero-shape-selector");
      if (sel) sel.style.left = left;
    } else if (selectorMode === "radial") {
      repositionRadialDots();
    } else if (selectorMode === "slot") {
      const slot = document.getElementById("hero-shape-slot");
      if (slot) slot.style.right = (window.innerWidth - containerRight()) + "px";
    }
    if (scrollProgressBar && selectorMode === "radial") {
      scrollProgressBar.style.right = `${window.innerWidth - containerRight()}px`;
    }
    const isMobile = window.innerWidth <= 768;
    const bio = document.getElementById("bio");
    if (bio) {
      if (isMobile) {
        bio.style.left = "";
        bio.style.right = "";
      } else {
        bio.style.left = left;
        bio.style.right = (window.innerWidth - containerRight()) + "px";
      }
    }
    const headline = document.getElementById("headline");
    if (headline) {
      if (isMobile) {
        headline.style.left = "";
      } else {
        headline.style.left = left;
      }
    }
    let w = host.clientWidth;
    let h = host.clientHeight;
    if (w <= 0) w = window.innerWidth;
    if (h <= 0) h = window.innerHeight;
    w = Math.max(1, w);
    h = Math.max(1, h);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h, true);
  }
  resize();
  window.addEventListener("resize", resize);
  new ResizeObserver(() => resize()).observe(host);
  requestAnimationFrame(() => resize());

  // ── Companion 2D draw ────────────────────────────────────────────────────
  function drawCompanion(sp: number, t: number) {
    if (!companionCtx || !companionCanvas || selectedShape === null) return;
    const size = CURSOR_RING_PX;
    const half = size / 2;
    const projScale = (half * 0.62) / 1.04;

    companionCtx.clearRect(0, 0, size, size);

    const splitCount = Math.floor(maxSplitCount * sp);
    if (splitCount === 0) return;

    const particlePhase = smoothstep01(sp);
    companionCtx.globalAlpha = Math.min(1, particlePhase * 2.5);
    companionCtx.fillStyle = "#ffffff";

    const cosY = Math.cos(t * 0.34);
    const sinY = Math.sin(t * 0.34);
    const cosX = Math.cos(t * 0.22);
    const sinX = Math.sin(t * 0.22);
    const target = companionTargets[selectedShape];

    for (let slot = 0; slot < splitCount; slot++) {
      const p = slot * 3;
      const lx = (companionCloudSource[p]     ?? 0) * (1 - particlePhase) + (target[p]     ?? 0) * particlePhase;
      const ly = (companionCloudSource[p + 1] ?? 0) * (1 - particlePhase) + (target[p + 1] ?? 0) * particlePhase;
      const lz = (companionCloudSource[p + 2] ?? 0) * (1 - particlePhase) + (target[p + 2] ?? 0) * particlePhase;
      const rx1 = lx * cosY + lz * sinY;
      const ry1 = ly;
      const rz1 = -lx * sinY + lz * cosY;
      const rx = rx1;
      const ry = ry1 * cosX - rz1 * sinX;
      const sx = half + rx * projScale;
      const sy = half - ry * projScale;
      companionCtx.beginPath();
      companionCtx.arc(sx, sy, 1.0, 0, Math.PI * 2);
      companionCtx.fill();
    }
  }

  // ── Tick ─────────────────────────────────────────────────────────────────
  function tick() {
    const dt = Math.min(clock.getDelta(), 0.1);
    const t = clock.elapsedTime;
    const c = centerPositionAttr.array as Float32Array;

    // ── Sphere build-up: scatter → sphere → normal ───────────────────────
    // sphereW: 0=normal, 1=locked to sphere surface
    // scatterW: 0=at sphere surface, 1=at scatter start positions
    // Morph cycle is frozen (morphActive=false) until lock fully ends
    let sphereW = 0;
    let scatterW = 0;
    if (sphereLockStartMs >= 0) {
      const elapsed = (performance.now() - sphereLockStartMs) / 1000;
      if (elapsed < SPHERE_BUILD_S) {
        // Converge: scatter → cloud
        sphereW  = 1;
        scatterW = 1 - smoothstep01(elapsed / SPHERE_BUILD_S);
      } else {
        // Done — activate morph cycle; MORPH_INITIAL_REST_S gives the cloud hold before first shape
        morphTimeOffset   = t;
        morphActive       = true;
        sphereLockStartMs = -1;
      }
    }

    // Notify loader of hero rotation state on first frame so it can sync
    if (!heroReadyFired) {
      heroReadyFired = true;
      window.dispatchEvent(new CustomEvent('hero-ready', {
        detail: {
          rotY: heroMode === 'interactive' ? t * 0.34 : t * 0.31,
          rotX: heroMode === 'interactive' ? t * 0.22 : 0,
        },
      }));
    }

    if (heroMode === "loop") {
      const rawMorphT = morphActive ? Math.max(0, t - morphTimeOffset - MORPH_INITIAL_REST_S) : 0;
      const { shapeA, shapeB, w } = loopMorphBlend(rawMorphT);
      const a = 1 - w;
      for (let i = 0; i < totalParticles; i++) {
        const p = i * 3;
        let x = shapeA[p]! * a + shapeB[p]! * w;
        let y = shapeA[p + 1]! * a + shapeB[p + 1]! * w;
        let z = shapeA[p + 2]! * a + shapeB[p + 2]! * w;
        if (sphereW > 0) {
          // Sphere target: blend between scatter start and sphere surface
          const sx = heroSphereScatter[p]!     * scatterW + cloud[p]!     * (1 - scatterW);
          const sy = heroSphereScatter[p + 1]! * scatterW + cloud[p + 1]! * (1 - scatterW);
          const sz = heroSphereScatter[p + 2]! * scatterW + cloud[p + 2]! * (1 - scatterW);
          x = x * (1 - sphereW) + sx * sphereW;
          y = y * (1 - sphereW) + sy * sphereW;
          z = z * (1 - sphereW) + sz * sphereW;
        }
        const spinX = x * Math.cos(t * 0.31) - z * Math.sin(t * 0.31);
        const spinZ = x * Math.sin(t * 0.31) + z * Math.cos(t * 0.31);
        c[p] = spinX * pointsScale;
        c[p + 1] = y * pointsScale + pointsVerticalBias;
        c[p + 2] = spinZ * pointsScale;
      }
      centerPositionAttr.needsUpdate = true;
      if (hasFinePointer) hud.style.display = "none";
    } else {
      // ── Interactive mode ──────────────────────────────────────────────
      const now = performance.now();
      if (activePointerId !== null && !holdActive) {
        holdActive = now - holdStartMs >= HOLD_THRESHOLD_MS;
      }

      // ── Sphere hover detection ────────────────────────────────────────
      const rect = canvas.getBoundingClientRect();
      const canvasCx = rect.left + rect.width / 2;
      const canvasCy = rect.top + rect.height / 2;
      const dx = cursorTargetX - canvasCx;
      const dy = cursorTargetY - canvasCy;
      const sphereHoverRadius = Math.min(rect.width, rect.height) * 0.20;
      const rawOverSphere = Math.hypot(dx, dy) < sphereHoverRadius;
      const overSphere = rawOverSphere && isInHero && activePointerId === null && cursorVisible;
      const targetHover = overSphere ? 1 : 0;
      sphereHoverProgress += (targetHover - sphereHoverProgress) * Math.min(1, dt * 3);

      // ── Ring opacity — driven by sphere hover (if not selector-hovering) ──
      if (cursorVisible) {
        const ringOpacity = (Math.max(0, 1 - sphereHoverProgress)).toFixed(3);
        if (cursorDiv && !cursorDiv.classList.contains("ring-selector-hover")) {
          cursorDiv.style.opacity = ringOpacity;
        }
        if (subRingDiv && !subRingDiv.classList.contains("ring-selector-hover")) {
          subRingDiv.style.opacity = ringOpacity;
        }
      }

      // ── Ring border brightness ramp (0–300ms hold feedback) ──────────
      if (activePointerId !== null && !holdActive && cursorDiv && !cursorDiv.classList.contains("ring-collapsed")) {
        const rampProgress = Math.min(1, (now - holdStartMs) / HOLD_THRESHOLD_MS);
        const borderOpacity = 0.55 + 0.45 * rampProgress;
        cursorDiv.style.borderColor = `rgba(255,255,255,${borderOpacity.toFixed(2)})`;
      }

      // ── Hold activation edge: highlight ring; release: dim back ──────
      if (holdActive && !wasHoldActive) {
        wasHoldActive = true;
        holdActivatedAt = now;
        if (cursorDiv) cursorDiv.style.borderColor = "rgba(255,255,255,1.0)";
      }
      if (!holdActive && wasHoldActive) {
        wasHoldActive = false;
        holdActivatedAt = 0;
        if (cursorDiv) cursorDiv.style.borderColor = "rgba(255,255,255,0.55)";
      }

      // ── Split progress — gated by shape selection + delay ────────────
      const splitReady = holdActive && holdActivatedAt > 0
        && (now - holdActivatedAt) >= SPLIT_DELAY_MS
        && selectedShape !== null;
      const targetSplit = splitReady ? 1 : 0;
      const splitRate = targetSplit > splitProgress ? 0.7 : 2.5;
      splitProgress += (targetSplit - splitProgress) * Math.min(1, dt * splitRate);
      splitProgress = Math.min(1, Math.max(0, splitProgress));
      // Snap to zero once imperceptibly small so diffuseAlpha restores the ring cleanly
      if (splitProgress < 0.005) splitProgress = 0;

      // Update cursor ring and sub-ring positions (same lerp)
      if (cursorDiv) {
        cursorX += (cursorTargetX - cursorX) * Math.min(1, dt * 6);
        cursorY += (cursorTargetY - cursorY) * Math.min(1, dt * 6);
        cursorDiv.style.left = `${cursorX}px`;
        cursorDiv.style.top = `${cursorY}px`;
      }
      if (subRingDiv) {
        subRingDiv.style.left = `${cursorX + SUB_RING_OFFSET}px`;
        subRingDiv.style.top = `${cursorY}px`;
      }

      // Center cloud — depletes as splitProgress grows
      const depletedCount = Math.floor(maxSplitCount * splitProgress);
      centerRotEuler.set(t * 0.22, t * 0.34, 0);
      for (let i = 0; i < totalParticles; i++) {
        const p = i * 3;
        if (splitRank[i]! < depletedCount) {
          c[p] = OFFSCREEN; c[p + 1] = OFFSCREEN; c[p + 2] = OFFSCREEN;
        } else {
          const src = baseCloudVectors[i]!;
          if (sphereW > 0) {
            // Convergence: blend scatter start positions toward cloud
            const bx = heroSphereScatter[p]!     * scatterW + src.x * (1 - scatterW);
            const by = heroSphereScatter[p + 1]! * scatterW + src.y * (1 - scatterW);
            const bz = heroSphereScatter[p + 2]! * scatterW + src.z * (1 - scatterW);
            tempCenter.set(bx, by, bz).applyEuler(centerRotEuler);
          } else {
            tempCenter.copy(src).applyEuler(centerRotEuler);
          }
          c[p] = tempCenter.x * pointsScale;
          c[p + 1] = tempCenter.y * pointsScale + pointsVerticalBias;
          c[p + 2] = tempCenter.z * pointsScale;
        }
      }
      centerPositionAttr.needsUpdate = true;

      drawCompanion(splitProgress, t);

      // ── HUD — follows cursor ring, offset below ───────────────────────
      if ((isInHero || overVideo) && cursorDiv) {
        hud.style.display = "";
        hud.style.left = `${cursorX}px`;
        hud.style.top = `${cursorY + CURSOR_RING_PX / 2 + 15}px`;
      }

      // HUD text — loader locks text until overlay is gone
      const aboveBioText = bioWrapEl
        ? cursorTargetY < bioWrapEl.getBoundingClientRect().top
        : false;
      if ((window as any).__loaderActive) {
        hud.textContent   = "Ideas are in the air";
        hud.style.opacity = "1";
      } else if (hoverInfoLabel !== null) {
        hud.style.display = "";
        hud.style.left    = `${cursorX}px`;
        hud.style.top     = `${cursorY + CURSOR_RING_PX / 2 + 15}px`;
        hud.textContent   = "Learn more";
        hud.style.opacity = "1";
      } else if (inModal && overVideo && !overBio && aboveBioText) {
        hud.textContent = "Justis";
        hud.style.opacity = "1";
      } else if (inModal) {
        hud.style.display = "none";
      } else if (hoverShapeName !== null) {
        hud.innerHTML =
          `<span style="display:block;font-size:9px;font-weight:600;letter-spacing:0.15em;opacity:0.55;margin-bottom:2px;text-transform:uppercase">DIRECTION</span>` +
          `<span style="display:block">${hoverShapeName}</span>`;
      } else if (activePointerId !== null && selectedShape === null) {
        hud.textContent = "No direction selected";
      } else if (activePointerId !== null && !holdActive) {
        hud.textContent = "Hold to gather...";
      } else {
        const p = splitProgress;
        if (p >= 0.15) {
          if (activePointerId === null) {
            hud.textContent = "Diffusing...";           // only while companions are visible
          } else if (p < 0.25) {
            hud.textContent = "Gathering ideas...";
          } else if (p < 0.55) {
            hud.textContent = "Structuring signal...";
          } else if (p < 0.92) {
            hud.textContent = "Defining form...";
          } else {
            hud.textContent = "Transformed";
          }
        } else if (overSphere) {
          hud.textContent = "Ideas are in the air";
        } else if (selectedShape !== null) {
          const shapeName = selectedShape.charAt(0).toUpperCase() + selectedShape.slice(1);
          hud.textContent = `Transform to ${shapeName}`;
        } else {
          hud.textContent = "click + hold to transform";
        }
      }
    }

    // ── Particle size — fixed (no hover size change) ──────────────────────
    centerMaterial.size = BASE_PARTICLE_SIZE;
    centerMaterial.needsUpdate = true;

    if (selectorMode === "radial") {
      radialVelocity     *= Math.exp(-RADIAL_FRICTION * dt);
      spinInVelocity     *= Math.exp(-SPIN_IN_FRICTION * dt);
      radialAngleCurrent += (radialVelocity + spinInVelocity) * dt;
      repositionRadialDots();
    }

    // ── Selector dot state ────────────────────────────────────────────────
    // Idle (no selection): solid white. Shape selected: updateShapeSelector() handles it.
    // Holding with no direction chosen: fast flash to cue the user.
    if (shapeSelectorDots.length > 0) {
      if (activePointerId !== null && selectedShape === null) {
        // Slow trapezoid pulse — draws attention without feeling flashy
        const FLASH_PERIOD = 3.0;
        const fp = (performance.now() / 1000) % FLASH_PERIOD / FLASH_PERIOD;
        let flashOpacity: number;
        if (fp < 0.30) {
          const u = fp / 0.30;
          flashOpacity = 0.20 + 0.70 * (u * u * (3 - 2 * u));   // slow rise
        } else if (fp < 0.50) {
          flashOpacity = 0.90;                                      // hold at peak
        } else if (fp < 0.80) {
          const u = (fp - 0.50) / 0.30;
          flashOpacity = 0.90 - 0.70 * (u * u * (3 - 2 * u));   // slow fall
        } else {
          flashOpacity = 0.20;                                      // dark pause
        }
        shapeSelectorDots.forEach(dot => {
          const svg = dot.querySelector("svg") as HTMLElement | null;
          if (svg) svg.style.opacity = flashOpacity.toFixed(3);
          if (selectorMode === "radial") {
            (dot as HTMLElement).style.borderColor =
              `rgba(255,255,255,${flashOpacity.toFixed(3)})`;
          }
        });
      } else if (selectedShape === null) {
        // Idle: ensure solid white (guards transition back from flash state)
        shapeSelectorDots.forEach(dot => {
          const svg = dot.querySelector("svg") as HTMLElement | null;
          if (svg) svg.style.opacity = "1";
          if (selectorMode === "radial") {
            (dot as HTMLElement).style.borderColor = "rgba(255,255,255,0.85)";
          }
        });
      }
      // selectedShape !== null: updateShapeSelector() already set dimmed/active on click
    }
    if (selectorMode === "slot" && slotTrack) {
      slotCurrentY += (-slotRawTarget - slotCurrentY) * Math.min(1, dt * 8);
      slotTrack.style.transform = `translateY(${slotCurrentY}px)`;
      const rawIdx   = Math.round(-slotCurrentY / SLOT_ITEM_H);
      const activeIdx = ((rawIdx % ALL_SHAPES.length) + ALL_SHAPES.length) % ALL_SHAPES.length;
      const snapped  = ALL_SHAPES[activeIdx]!;
      // Commit selection once the track has settled within 1px of the snap position
      if (Math.abs(slotCurrentY - (-rawIdx * SLOT_ITEM_H)) < 1) {
        if (selectedShape !== snapped) {
          selectedShape = snapped;
          updateShapeSelector();
          updateShapeIndicator();
        }
      }
    }
    renderer.render(scene, camera);
  }

  renderer.setAnimationLoop(tick);

  // Mobile: show "Ideas are in the air" during the loader, fade it out when the loader starts fading.
  // This ensures the label clears before particles become prominent — no overlap.
  if (!hasFinePointer && heroMode === 'loop') {
    hud.textContent = "Ideas are in the air";
    hud.style.left = "50%";
    hud.style.top = "50%";
    hud.style.transform = "translate(-50%, -50%)";
    hud.style.opacity = "1";
    hud.style.display = "";
    const fadeOutHud = () => {
      hud.style.transition = "opacity 0.8s ease";
      hud.style.opacity = "0";
      setTimeout(() => { hud.style.display = "none"; }, 900);
    };
    window.addEventListener('loader-fading', fadeOutHud, { once: true });
    // Fallback: hide after 5s if loader event never fires (dev / no loader)
    setTimeout(() => { if (hud.style.display !== "none") fadeOutHud(); }, 5000);
  }

  const modalCtrl = initAboutModal({
    onItemHover: label => { hoverInfoLabel = label; },
    onOpen: () => {
      shapeSelectorDots.forEach(dot => {
        dot.style.transition = "opacity 300ms ease";
        dot.style.opacity = "0";
        dot.style.pointerEvents = "none";
      });
    },
    onClose: () => {
      shapeSelectorDots.forEach(dot => {
        dot.style.transition = "opacity 300ms ease";
        dot.style.opacity = "1";
        dot.style.pointerEvents = "";
      });
    },
  });
  // Collapse cursor ring while hovering the about modal; track modal + video state for HUD
  const aboutModalEl = document.getElementById("about-modal");
  if (aboutModalEl) {
    aboutModalEl.addEventListener("mouseenter", () => {
      inModal = true;
      if (cursorDiv)  cursorDiv.classList.add("ring-collapsed");
      if (subRingDiv) subRingDiv.classList.add("ring-collapsed");
    });
    aboutModalEl.addEventListener("mouseleave", () => {
      inModal = false;
      overVideo = false;
      overBio = false;
      if (cursorDiv)  cursorDiv.classList.remove("ring-collapsed");
      if (subRingDiv) subRingDiv.classList.remove("ring-collapsed");
    });
  }
  // "Justis" HUD label when hovering the video area (but not the bio text overlay)
  const photoWrapEl = document.getElementById("am-photo-wrap");
  if (photoWrapEl) {
    photoWrapEl.addEventListener("mouseenter", () => { overVideo = true; });
    photoWrapEl.addEventListener("mouseleave", () => { overVideo = false; });
  }
  const bioWrapEl = document.getElementById("am-bio-wrap");
  if (bioWrapEl) {
    bioWrapEl.addEventListener("mouseenter", () => { overBio = true; });
    bioWrapEl.addEventListener("mouseleave", () => { overBio = false; });
  }
  initSkillsGrid();
  initSliderIndicator();
}

init();
